/**
 * Sessions — live query-tab connections. Admins see every user's sessions; a
 * normal user sees only their own. The point is to spot and kill an orphaned
 * in-transaction session (e.g. a tab whose browser died mid-transaction) that's
 * holding locks, instead of waiting for MySQL's wait_timeout.
 */
import { createResource, createSignal, onMount, onCleanup, For, Show } from 'solid-js';
import { listQuerySessions, killQuerySession } from '../lib/querysession.js';
import { currentUser } from '../lib/auth-store.js';
import { toast } from '../lib/toast.js';

export default function Sessions() {
  const [sessions, { refetch }] = createResource(listQuerySessions);
  const [now, setNow] = createSignal(Date.now());
  const [killing, setKilling] = createSignal(null);
  const isAdmin = () => currentUser()?.role === 'admin';

  // Tick so the "idle" column stays live.
  onMount(() => {
    const h = setInterval(() => setNow(Date.now()), 1000);
    onCleanup(() => clearInterval(h));
  });

  const ago = (unix) => {
    const s = Math.max(0, Math.floor(now() / 1000) - unix);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  };
  const status = (s) => (s.running ? 'running' : s.inTransaction ? 'in transaction' : 'idle');

  const kill = async (s) => {
    const label = s.connectionName || `connection ${s.connectionId}`;
    if (!confirm(`Kill this query session on ${label}? Any open transaction is rolled back and its locks are released.`)) return;
    setKilling(s.id);
    try {
      await killQuerySession(s.id);
      toast.success('Session killed');
      refetch();
    } catch (e) {
      toast.error('Kill failed: ' + e.message);
    } finally {
      setKilling(null);
    }
  };

  return (
    <div class="page">
      <header class="page-header">
        <div>
          <h1>Query Sessions</h1>
          <p class="muted">
            {isAdmin() ? 'Live query-tab connections across all users.' : 'Your live query-tab connections.'}
            {' '}Kill an orphaned <strong>in-transaction</strong> session to release its locks.
          </p>
        </div>
        <button class="btn btn-ghost" onClick={() => { setNow(Date.now()); refetch(); }} disabled={sessions.loading}>↻ Refresh</button>
      </header>

      <Show when={sessions()} fallback={<div class="muted">Loading…</div>}>
        <Show
          when={sessions().length > 0}
          fallback={<div class="empty-state"><h2>No active sessions</h2><p class="muted">Open a query tab to start one.</p></div>}
        >
          <div class="mini-grid-wrap">
            <table class="mini-grid">
              <thead>
                <tr>
                  <Show when={isAdmin()}><th>User</th></Show>
                  <th>Connection</th>
                  <th>Database</th>
                  <th>Thread</th>
                  <th>Status</th>
                  <th>Idle</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <For each={sessions()}>
                  {(s) => (
                    <tr class={s.inTransaction && !s.running ? 'session-orphan-risk' : ''}>
                      <Show when={isAdmin()}><td>{s.username || s.userId}</td></Show>
                      <td>{s.connectionName || s.connectionId}</td>
                      <td class="mono">{s.database || <span class="muted">—</span>}</td>
                      <td class="mono">{s.threadId}</td>
                      <td><span class={`session-status session-${status(s).replace(/\s/g, '-')}`}>{status(s)}</span></td>
                      <td>{ago(s.lastActiveUnix)}</td>
                      <td style="text-align:right;">
                        <button class="btn btn-sm btn-danger" disabled={killing() === s.id} onClick={() => kill(s)}>
                          {killing() === s.id ? 'Killing…' : 'Kill'}
                        </button>
                      </td>
                    </tr>
                  )}
                </For>
              </tbody>
            </table>
          </div>
        </Show>
      </Show>
    </div>
  );
}
